import React, { useState, useEffect } from 'react';
import Badge from '@/components/common/Badge';
import Card, { CardBody } from '@/components/common/Card';
import ConfirmDialog, { DeleteIcon } from '@/components/common/ConfirmDialog';
import { formatCurrency } from '@/utils/formatters';
import { FaBoxOpen, FaTag, FaWarehouse, FaExclamationTriangle, FaPen, FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import { normalizeDataObject } from '../../utils/normalizationUtils';

/**
 * Componente para mostrar un repuesto individual
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.repuesto - Datos del repuesto
 * @param {Array} props.sucursales - Lista de sucursales disponibles
 * @param {Array} props.categorias - Lista de categorías disponibles
 * @param {function} props.onEdit - Función para editar el repuesto
 * @param {function} props.onAdjustStock - Función para ajustar el stock
 * @param {function} props.onDelete - Función para eliminar el repuesto
 * @param {function} props.onToggleStatus - Función para cambiar el estado activo/inactivo
 * @returns {JSX.Element} - Componente de ítem de repuesto
 */
const RepuestoItem = ({ repuesto, sucursales = [], categorias = [], onEdit, onAdjustStock, onDelete, onToggleStatus }) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Normalizar datos del repuesto para manejar cualquier formato
  const normalizedRepuesto = normalizeDataObject(repuesto, 'repuesto');

  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Comprobar inicialmente
    checkMobile();

    // Actualizar cuando cambie el tamaño de la ventana
    window.addEventListener('resize', checkMobile);

    // Limpiar al desmontar
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Función para obtener el color de borde según el estado del stock
  const getBorderColor = () => {
    const stock = normalizedRepuesto.stock || normalizedRepuesto.Stock || normalizedRepuesto.StockSucursal || 0;
    const stockMinimo = normalizedRepuesto.stockMinimo || normalizedRepuesto.StockMinimo || normalizedRepuesto.stock_minimo || normalizedRepuesto.StockMinimoSucursal || 0;
    
    if (stock === 0) {
      return 'border-red-500';
    } else if (stock <= stockMinimo) {
      return 'border-yellow-500';
    } else {
      return 'border-green-500';
    }
  };

  // Determinar estado de stock para el badge
  const getStockStatus = () => {
    const stock = normalizedRepuesto.stock || normalizedRepuesto.Stock || normalizedRepuesto.StockSucursal || 0;
    const stockMinimo = normalizedRepuesto.stockMinimo || normalizedRepuesto.StockMinimo || normalizedRepuesto.stock_minimo || normalizedRepuesto.StockMinimoSucursal || 0;
    const estaDisponible = normalizedRepuesto.disponible !== false && normalizedRepuesto.Disponible !== false;
    
    // Si no está disponible, mostrar solo el estado de disponibilidad
    if (!estaDisponible) {
      return null; // No mostrar badge de stock
    }
    
    if (stock === 0) {
      return { label: 'Sin stock', variant: 'danger' };
    } else if (stock <= stockMinimo) {
      return { label: 'Stock bajo', variant: 'warning' };
    } else {
      return { label: 'En stock', variant: 'success' };
    }
  };

  const stockStatus = getStockStatus();

  // Determinar estado de disponibilidad para el badge
  const getEstadoBadge = () => {
    const estaDisponible = normalizedRepuesto.disponible !== false && normalizedRepuesto.Disponible !== false;
    
    return estaDisponible ? 
      { label: 'Disponible', variant: 'success' } : 
      { label: 'No disponible', variant: 'danger' };
  };

  const estadoBadge = getEstadoBadge();

  // Función para obtener el nombre de la categoría por ID
  const getCategoriaNombre = () => {
    if (!normalizedRepuesto.categoriaId || normalizedRepuesto.categoria_id) return null;
    const categoria = categorias.find(cat => cat.id === normalizedRepuesto.categoriaId || normalizedRepuesto.categoria_id);
    return categoria ? categoria.nombre : null;
  };

  // Obtener nombre de la sucursal
  const getSucursalNombre = () => {
    if (!normalizedRepuesto.sucursal || normalizedRepuesto.Sucursal || !Array.isArray(normalizedRepuesto.sucursal || normalizedRepuesto.Sucursal) || normalizedRepuesto.sucursal || normalizedRepuesto.Sucursal.length === 0) return 'Desconocida';
    const sucursalId = normalizedRepuesto.sucursal || normalizedRepuesto.Sucursal[0];
    const sucursal = sucursales.find(s => s.id === sucursalId);
    return sucursal ? sucursal.Nombre : 'Desconocida';
  };

  return (
    <>
      <Card
        hover
        className={`mb-3 border-l-4 ${getBorderColor()} transition-all duration-300`}
      >
        <CardBody className="p-0">
          {isMobile ? (
            // Versión móvil
            <div>
              <div className="p-4">
                {/* Nombre del repuesto */}
                <div className="mb-3">
                  <h3 className="text-lg font-semibold">{normalizedRepuesto.nombre || normalizedRepuesto.Nombre}</h3>
                </div>

                {/* Badges de categoría, disponibilidad y stock */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {getCategoriaNombre() && (
                    <Badge variant="info" size="xs">{getCategoriaNombre()}</Badge>
                  )}
                  <Badge
                    variant={estadoBadge.variant}
                    size="sm"
                  >
                    {estadoBadge.label}
                  </Badge>
                  {stockStatus && (
                    <Badge
                      variant={stockStatus.variant}
                      size="sm"
                    >
                      {stockStatus.label}
                    </Badge>
                  )}
                </div>

                {/* Información del repuesto */}
                <div className="text-sm">
                  <div className="flex items-center mb-2">
                    <FaTag className="mr-2 text-gray-500" />
                    <span className="text-gray-300">
                      Venta: {formatCurrency(normalizedRepuesto.precioVenta || normalizedRepuesto.PrecioVenta || normalizedRepuesto.precio || normalizedRepuesto.Precio || normalizedRepuesto.precio || normalizedRepuesto.PrecioSucursal || 0)}
                      {(normalizedRepuesto.precioCompra || normalizedRepuesto.PrecioCompra > 0) && (
                        <span className="text-gray-400 text-xs ml-2">
                          (Compra: {formatCurrency(normalizedRepuesto.precioCompra || normalizedRepuesto.PrecioCompra)})
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="flex items-center mb-2">
                    <FaBoxOpen className="mr-2 text-gray-500" />
                    <span className="text-gray-300">
                      Stock: {normalizedRepuesto.stock || normalizedRepuesto.Stock || normalizedRepuesto.StockSucursal || 0} / Mín: {normalizedRepuesto.stockMinimo || normalizedRepuesto.StockMinimo || normalizedRepuesto.stock_minimo || normalizedRepuesto.StockMinimoSucursal || 0}
                    </span>
                  </div>

                  {normalizedRepuesto.ubicacion || normalizedRepuesto.Ubicacion && (
                    <div className="flex items-center mb-2">
                      <FaWarehouse className="mr-2 text-gray-500" />
                      <span className="text-gray-300">
                        Ubicación: {normalizedRepuesto.ubicacion || normalizedRepuesto.Ubicacion}
                      </span>
                    </div>
                  )}
                </div>

                {/* Alerta de stock bajo - solo si está disponible */}
                {(() => {
                  const stock = normalizedRepuesto.stock || normalizedRepuesto.Stock || normalizedRepuesto.StockSucursal || 0;
                  const stockMinimo = normalizedRepuesto.stockMinimo || normalizedRepuesto.StockMinimo || normalizedRepuesto.stock_minimo || normalizedRepuesto.StockMinimoSucursal || 0;
                  const tieneDetalle = normalizedRepuesto.tieneDetalleEnSucursal || normalizedRepuesto.TieneDetalleEnSucursal || (normalizedRepuesto.stock !== undefined || normalizedRepuesto.Stock !== undefined || normalizedRepuesto.StockSucursal !== undefined);
                  const estaDisponible = normalizedRepuesto.disponible !== false && normalizedRepuesto.Disponible !== false;
                  
                  if (estaDisponible && tieneDetalle && stock != null && stockMinimo != null && stock <= stockMinimo) {
                    return (
                      <div className="mt-3 text-yellow-400 text-xs flex items-center">
                        <FaExclamationTriangle className="mr-1" />
                        {stock === 0 ? (
                          'Sin stock disponible, realizar pedido.'
                        ) : (
                          `Stock por debajo del mínimo recomendado (${stockMinimo}).`
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Botones móviles */}
              <div className="flex justify-between border-t border-gray-700 bg-gray-800">
                <button
                  onClick={() => onEdit(repuesto)}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-yellow-500"
                  title="Editar"
                >
                  <FaPen className="text-lg" />
                  <span className="text-xs mt-1">Editar</span>
                </button>

                <button
                  onClick={() => onAdjustStock(repuesto)}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-blue-500"
                  title="Ajustar Stock"
                >
                  <FaBoxOpen className="text-lg" />
                  <span className="text-xs mt-1">Stock</span>
                </button>

                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="flex-1 py-3 flex flex-col items-center justify-center text-red-500"
                  title="Eliminar"
                >
                  <FaTrash className="text-lg" />
                  <span className="text-xs mt-1">Eliminar</span>
                </button>
              </div>
            </div>
          ) : (
            // Versión escritorio
            <div className="flex justify-between items-center p-4">
              {/* Toda la información del repuesto */}
              <div className="flex flex-col md:flex-row w-full">
                {/* Nombre y badges */}
                <div className="flex items-center min-w-[220px] w-full md:w-1/2 mb-2 md:mb-0">
                  <div className="mr-2 bg-blue-600 bg-opacity-20 p-2 rounded-full">
                    <FaBoxOpen className="text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold flex items-center mr-4">{normalizedRepuesto.nombre || normalizedRepuesto.Nombre}
                    {getCategoriaNombre() && (
                      <Badge variant="info" size="xs" className="ml-2">{getCategoriaNombre()}</Badge>
                    )}
                  </h3>
                  <Badge
                    variant={estadoBadge.variant}
                    size="sm"
                    className="ml-2"
                  >
                    {estadoBadge.label}
                  </Badge>
                  {stockStatus && (
                    <Badge
                      variant={stockStatus.variant}
                      size="sm"
                      className="ml-2"
                    >
                      {stockStatus.label}
                    </Badge>
                  )}
                </div>
                {/* Info de stock, precio y ubicación */}
                <div className="flex flex-col md:flex-row md:items-center w-full md:w-1/2 text-sm gap-x-8">
                  <div className="flex items-center mb-1 md:mb-0">
                    <FaTag className="mr-2 text-gray-500" />
                    <span className="text-gray-300">
                      Venta: {formatCurrency(normalizedRepuesto.precioVenta || normalizedRepuesto.PrecioVenta || normalizedRepuesto.precio || normalizedRepuesto.Precio || normalizedRepuesto.precio || normalizedRepuesto.PrecioSucursal || 0)}
                      {(normalizedRepuesto.precioCompra || normalizedRepuesto.PrecioCompra > 0) && (
                        <span className="text-gray-400 text-xs block">
                          Compra: {formatCurrency(normalizedRepuesto.precioCompra || normalizedRepuesto.PrecioCompra)}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center mb-1 md:mb-0">
                    <FaBoxOpen className="mr-2 text-gray-500" />
                    <span className="text-gray-300">
                      Stock: {normalizedRepuesto.stock || normalizedRepuesto.Stock || normalizedRepuesto.StockSucursal || 0} / Mín: {normalizedRepuesto.stockMinimo || normalizedRepuesto.StockMinimo || normalizedRepuesto.stock_minimo || normalizedRepuesto.StockMinimoSucursal || 0}
                    </span>
                  </div>
                  {normalizedRepuesto.ubicacion || normalizedRepuesto.Ubicacion && (
                    <div className="flex items-center">
                      <FaWarehouse className="mr-2 text-gray-500" />
                      <span className="text-gray-300">
                        {normalizedRepuesto.ubicacion || normalizedRepuesto.Ubicacion}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {/* Alerta de stock bajo - solo si está disponible */}
              {(() => {
                const stock = normalizedRepuesto.stock || normalizedRepuesto.Stock || normalizedRepuesto.StockSucursal || 0;
                const stockMinimo = normalizedRepuesto.stockMinimo || normalizedRepuesto.StockMinimo || normalizedRepuesto.stock_minimo || normalizedRepuesto.StockMinimoSucursal || 0;
                const tieneDetalle = normalizedRepuesto.tieneDetalleEnSucursal || normalizedRepuesto.TieneDetalleEnSucursal || (normalizedRepuesto.stock !== undefined || normalizedRepuesto.Stock !== undefined || normalizedRepuesto.StockSucursal !== undefined);
                const estaDisponible = normalizedRepuesto.disponible !== false && normalizedRepuesto.Disponible !== false;
                
                if (estaDisponible && tieneDetalle && stock != null && stockMinimo != null && stock <= stockMinimo) {
                  return (
                    <div className="text-yellow-400 text-xs flex items-center mt-2">
                      <FaExclamationTriangle className="mr-1" />
                      {stock === 0 ? (
                        'Sin stock disponible, realizar pedido.'
                      ) : (
                        `Stock por debajo del mínimo recomendado (${stockMinimo}).`
                      )}
                    </div>
                  );
                }
                return null;
              })()}
              {/* Botones pequeños en línea horizontal */}
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => onEdit(repuesto)}
                  className="text-white bg-yellow-600 hover:bg-yellow-700 px-2 py-1 rounded"
                  title="Editar"
                >
                  <FaPen />
                </button>

                <button
                  onClick={() => onAdjustStock(repuesto)}
                  className="text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded"
                  title="Ajustar Stock"
                >
                  <FaBoxOpen />
                </button>

                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded"
                  title="Eliminar"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Diálogo de confirmación para eliminar */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => {
          onDelete(normalizedRepuesto.id || repuesto.id);
          setIsDeleteDialogOpen(false);
        }}
        title="Eliminar repuesto"
        message={`¿Estás seguro de que deseas eliminar el repuesto "${normalizedRepuesto.nombre || normalizedRepuesto.Nombre}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmVariant="danger"
        icon={<DeleteIcon />}
      />
    </>
  );
};

export default RepuestoItem;